import React, { FC, Fragment, useState, useEffect } from 'react';
import { getShortLanguageCode } from 'utils/format';
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { organizationLookupTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { geocodeAddress } from 'utils/google-places';
import { ClinicFormData } from 'models/forms';
import ChildClinicTypeSelect, { ChildClinicType } from './child-clinic-type/child-clinic-type.component';
import ClinicForm from 'components/shared/clinic-form/clinic-form.component';
import OrganizationLookup, {
    OrganizationLookupFormData,
} from 'components/shared/organization-lookup/organization-lookup.component';
import { Clinic } from 'models/clinic';
import { addClinicTemplate, addLocalClinicTemplate } from 'apollo/templates/clinic';
import { getEnabledCountries } from 'utils/country-helper';
import { Country } from 'models/country';
import { excludeObjectKeys } from 'utils/exclude-object-key';
import { OrgDropdown } from 'models/user';

type AddClinicDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onClinicAdded: () => void;
    allowFullEdit?: boolean;
    isChildClinic?: boolean;
    isLocalClinic?: boolean;
    /** The parent clinic; required for the child clinic flow. */
    parentClinic?: Clinic;
};
const AddClinicDialog: FC<AddClinicDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onClinicAdded,
    allowFullEdit,
    isChildClinic,
    isLocalClinic,
    parentClinic,
}: AddClinicDialogProps) => {
    const EnabledCountries = getEnabledCountries();
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [activeStep, setActiveStep] = useState(isChildClinic ? 'child_clinic_type' : 'lookup');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [clinic, setClinic] = useState<Partial<Clinic>>();
    const [countryCode, setCountryCode] = useState<string>();
    const scandinavianCountry = EnabledCountries.find((country) => country.alpha2Code === 'SE');
    const [country] = useState<Country | undefined>(scandinavianCountry);
    const parentOrganizationId = parentClinic?._id;
    const parentOrganizationNumber = parentClinic?.organizationNumber;
    const parentOrganizationOfficialName = parentClinic?.officialName;

    const [organizationLookup] = useLazyQuery(organizationLookupTemplate, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            setIsLoading(false);
            if (error.message.includes('HitHorizons: No results found')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
                return;
            }
            if (error.message.includes('organization with same hitHorizonsId already exists')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            showBoundary(error);
        },
        onCompleted: async ({ organizationLookup }) => {
            organizationLookup = organizationLookup[0];
            setIsLoading(false);
            if (organizationLookup.existsInDb) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            if (countryCode) {
                setClinic({
                    hitHorizonsId: organizationLookup.hitHorizonsId,
                    name: organizationLookup.name,
                    type: organizationLookup.type,
                    officialName: organizationLookup.name,
                    organizationNumber: organizationLookup.organizationNumber,
                    contact: {
                        communication: { site: organizationLookup.site || undefined },
                        address: await geocodeAddress(organizationLookup.address, getShortLanguageCode()),
                    },
                    invoicing: {
                        vatNo: organizationLookup.vatNumber,
                    },
                });
                setActiveStep('add_clinic');
            }
        },
    });

    const [addClinic] = useMutation(addClinicTemplate, {
        onCompleted: ({}) => {
            onClinicAdded();
            resetForm();
        },
        onError(error) {
            handleCloseDialog();
            showBoundary(error);
        },
    });

    const [addLocalClinic] = useMutation(addLocalClinicTemplate, {
        onCompleted: ({}) => {
            onClinicAdded();
            resetForm();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (isLocalClinic) {
            setActiveStep('add_clinic');
        }
    }, [isLocalClinic]);

    const handleOrganizationSelected = (selectOrganizationForm: OrganizationLookupFormData) => {
        setIsLoading(true);
        setCountryCode(selectOrganizationForm.countryCode);
        organizationLookup({
            variables: {
                organizationNumber: selectOrganizationForm.organizationNumber,
                countryCode: selectOrganizationForm.countryCode,
                checkWhitelabel: false,
            },
        });
    };

    const handleCloseDialog = () => {
        onCloseDialog();
        resetForm();
    };

    const resetForm = () => {
        setActiveStep(isChildClinic ? 'child_clinic_type' : 'lookup');
        setErrorMessage(undefined);
        setIsLoading(false);
        setClinic(undefined);
        setCountryCode(undefined);
    };

    const handleFormSubmitted = (formData: ClinicFormData) => {
        if (!isLocalClinic) {
            const parentClinicId = isChildClinic ? parentOrganizationId : undefined;
            const crediFlow = { partyID: formData.crediflowPartyId };
            delete formData.crediflowPartyId;
            setIsLoading(true);
            addClinic({
                variables: {
                    clinic: excludeObjectKeys({ ...formData, parentClinicId, crediFlow }, [
                        'type' as unknown as keyof ClinicFormData,
                    ]),
                },
            });
        } else {
            // Super admin - Add Local clinic
            setIsLoading(true);
            const localLabOwner = (formData.localLabOwner as OrgDropdown)?._id;
            addLocalClinic({
                variables: {
                    clinic: excludeObjectKeys({ ...formData, localLabOwner }, [
                        'type' as unknown as keyof ClinicFormData,
                    ]),
                },
            });
        }
    };

    const handleManualAddClicked = () => {
        setActiveStep('add_clinic');
    };

    const handleTypeSelect = (type: ChildClinicType) => {
        switch (type) {
            case ChildClinicType.SAME_ORGANIZATION:
                setActiveStep('add_clinic');
                setClinic({
                    organizationNumber: parentOrganizationNumber,
                    officialName: parentOrganizationOfficialName,
                });
                break;
            case ChildClinicType.STANDALONE_ORGANIZATION:
                setActiveStep('lookup');
                break;
            default:
                break;
        }
    };

    const getDialogTitle = (): string => {
        if (isLocalClinic) {
            return translate('organization:ADD_CLINIC_LOCAL');
        }
        if (parentClinic) {
            return translate('organization:ADD_CHILD_CLINIC_TO', { clinicName: parentClinic.name });
        }
        return translate('organization:ADD_CLINIC');
    };

    const getStepContent = (step: string) => {
        switch (step) {
            case 'child_clinic_type':
                return (
                    <ChildClinicTypeSelect
                        onClose={handleCloseDialog}
                        onTypeSelected={handleTypeSelect}
                        parentOrganizationNumber={parentOrganizationNumber ?? ''}
                    />
                );
            case 'lookup':
                return (
                    <OrganizationLookup
                        onOrganizationSelected={handleOrganizationSelected}
                        onClose={handleCloseDialog}
                        country={country}
                        onOrganizationChanged={() => setErrorMessage(undefined)}
                        allowManualAdd={allowFullEdit}
                        onManualAddClicked={handleManualAddClicked}
                        manualTitle={translate('organization:ADD_CLINIC_MANUAL')}
                    />
                );
            case 'add_clinic':
                return (
                    <Fragment>
                        <ClinicForm
                            clinic={clinic || {}}
                            onClose={handleCloseDialog}
                            onFormSubmitted={handleFormSubmitted}
                            submitButtonText={
                                isLocalClinic
                                    ? translate('organization:ADD_CLINIC_LOCAL')
                                    : translate('organization:ADD_CLINIC')
                            }
                            allowFullEdit={allowFullEdit}
                            isLocalClinic={isLocalClinic}
                        />
                    </Fragment>
                );
            default:
                break;
        }
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen}>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {isLoading ? (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AddClinicDialog;
